/**
 * upload-image — 运营图片直传的统一 multipart 上传（书籍封面 / Banner 轮播图等共用）。
 *
 * 为何不走 apiRequest：apiRequest 恒设 `Content-Type: application/json`，会破坏 multipart boundary
 * （FormData 要让浏览器自设 multipart/form-data + boundary）。但**仍走 authedFetch** —— 它只并 headers、
 * 不碰 Content-Type，同 download-authed-csv / ProfileAvatar 的既有做法。
 *
 * **不再手注 Bearer、不再自己 clearSession**：那套写法在 access 过期时直接把运营踢下线，正是
 * 「登录约 15min 自动登出」的根因模式（`api/session-refresh.ts` 头注）。交给 authedFetch 后，
 * 401 会先续期重试，仅当 refresh 显式失效才登出。
 *
 * 响应仍是 `{ code, data }` envelope → 解出 data；非 OK 抛 AdminApiError（调用方统一 toast）。
 */

import {
  AdminApiError,
  type AdminErrorBody,
  AdminRequestAbortedError,
  AdminRequestTimeoutError,
} from '~/api/errors';
import { DEFAULT_REQUEST_TIMEOUT_MS } from '~/api/network-aware-fetch';
import { authedFetch } from '~/api/session-refresh';
import { newRequestId } from '~/lib/request-id';

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

/**
 * 把单个图片文件 multipart POST 到给定 admin 端点（字段名 `file`），返响应 envelope 的 data。
 * @param path admin API 路径（含前缀，如 `/api/v1/books/cover-upload`）
 */
export async function uploadImageFile<T>(path: string, file: File): Promise<T> {
  const fd = new FormData();
  fd.append('file', file);
  // authedFetch 内部经 network-aware-fetch：断网 / 域名被阻断时抛 AdminNetworkError（「无法连接
  // 服务器」），而不是让裸 TypeError 冒到调用方显示成浏览器方言的 "Failed to fetch"。
  // 同时本次上传会被登记进在飞写请求表（#356）—— 封面 / Banner 直传正是最容易卡住的那批。
  const res = await authedFetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'X-Request-Id': newRequestId() },
    body: fd,
  });
  let json: { code?: string; data?: T } | null;
  try {
    json = (await res.json()) as { code?: string; data?: T } | null;
  } catch (err) {
    // 中止类错误原样上抛（通知条取消已被 authedFetch 的 Proxy 转成 AdminRequestCanceledError，
    // name 不再是 AbortError）；吞成 null 会让「可能已落库」的上传报成通用失败
    if (err instanceof AdminRequestAbortedError) throw err;
    if ((err as { name?: string } | null)?.name === 'AbortError') {
      throw new AdminRequestTimeoutError('POST', DEFAULT_REQUEST_TIMEOUT_MS);
    }
    json = null;
  }
  if (!res.ok || json?.code !== 'OK' || json.data == null) {
    // 401 的登出决策归 authedFetch（仅 refresh 显式失效才 forceLogout），此处只抛错
    throw new AdminApiError(
      res.status,
      (json as AdminErrorBody | null) ?? {
        code: 'INTERNAL_ERROR',
        message: '图片上传失败',
        requestId: '',
      },
    );
  }
  return json.data;
}
